import { useQuery } from "@tanstack/react-query";
import type { GetStagesItem } from "@microtica/ms-engine-sdk";
import {
    Alert,
    Button,
    Card,
    Divider,
    Dropdown,
    Empty,
    Input,
    Modal,
    Select,
    Skeleton,
    Space,
    Statistic,
    Tag,
    Typography,
    message
} from "antd";
import type { MenuProps } from "antd";
import {
    AlertTriangle,
    CalendarClock,
    ChevronRight,
    Clock3,
    Cloud,
    Copy,
    MoreHorizontal,
    Pencil,
    Play,
    Plus,
    RefreshCw,
    Search,
    Server,
    Square,
    TimerReset,
    Trash2,
    XCircle
} from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useRecoilValue } from "recoil";
import { getCloudService, getEnvironmentService } from "../../backend";
import type { Scheduler } from "../../backend/cost-optimizer";
import { currentProjectState } from "../../recoil/project";
import RecentActivity from "./components/RecentActivity";
import ResourceInventory from "./components/ResourceInventory";
import CostPageHeader from "./components/CostPageHeader";
import ScheduleEditorDialog, {
    SchedulerAwsAccount,
    SchedulerEnvironment
} from "./components/ScheduleEditorDialog";
import {
    useSchedulerMutations,
    useSchedulerOperations,
    useSchedulerResources,
    useSchedulers
} from "./hooks/useCostOptimizer";
import "./CostOptimizer.less";

const { Text, Title } = Typography;

interface AwsEnvironment extends GetStagesItem {
    awsAccountId?: string;
    awsRegion?: string;
    region?: string;
}

const EMPTY_SUMMARY = {
    total: 0,
    managed: 0,
    running: 0,
    stopped: 0,
    transitioning: 0,
    failed: 0
};

function errorMessage(error: unknown) {
    const candidate = error as { response?: { data?: { message?: string } }; message?: string };
    return candidate.response?.data?.message || candidate.message || "The request failed. Try again.";
}

function formatDateTime(value?: string | null, timezone?: string) {
    if (!value) return "Not set";
    return new Intl.DateTimeFormat(undefined, {
        dateStyle: "medium",
        timeStyle: "short",
        timeZone: timezone
    }).format(new Date(value));
}

function formatDays(days: string[]) {
    const weekdays = ["MON", "TUE", "WED", "THU", "FRI"];
    if (weekdays.every(day => days.includes(day)) && days.length === 5) return "Mon to Fri";
    if (days.length === 7) return "Every day";
    return days.map(day => `${day[0]}${day.slice(1).toLowerCase()}`).join(", ");
}

function scheduleSummary(scheduler: Scheduler) {
    if (scheduler.mode === "manual") {
        return `On-demand · ${scheduler.lease.defaultMinutes / 60}h default lease`;
    }
    return `${formatDays(scheduler.automaticSchedule.daysOfWeek)} · ${scheduler.automaticSchedule.startTime} to ${scheduler.automaticSchedule.stopTime}`;
}

function statusDetails(scheduler: Scheduler) {
    const operation = scheduler.latestOperation;
    if (operation?.status === "queued" || operation?.status === "running") {
        return {
            label: operation.action === "start" ? "Starting" : "Stopping",
            color: "processing",
            className: "processing"
        };
    }
    if (operation?.status === "failed" || operation?.status === "partial") {
        return { label: "Needs attention", color: "error", className: "failed" };
    }
    if (scheduler.state.desiredState === "running") {
        return { label: "Running", color: "success", className: "running" };
    }
    return { label: "Stopped", color: "default", className: "stopped" };
}

export default function CostExplorer({ navigation }: { navigation?: React.ReactNode }) {
    const { projectId = "" } = useParams<{ projectId: string }>();
    const currentProject = useRecoilValue(currentProjectState);
    const schedulerEnabled = currentProject?.paymentPlan?.limitations?.savingSchedules !== false;
    const [selectedSchedulerId, setSelectedSchedulerId] = useState<string>();
    const [schedulerSearch, setSchedulerSearch] = useState("");
    const [editorOpen, setEditorOpen] = useState(false);
    const [editorScheduler, setEditorScheduler] = useState<Scheduler>();
    const [startOpen, setStartOpen] = useState(false);
    const [stopOpen, setStopOpen] = useState(false);
    const [extendOpen, setExtendOpen] = useState(false);
    const [leaseMinutes, setLeaseMinutes] = useState(480);
    const [mutationError, setMutationError] = useState("");

    const environmentsQuery = useQuery({
        queryKey: ["cost-optimizer", projectId, "environments"],
        queryFn: async () => (await getEnvironmentService().getStages(projectId)).data.stages as AwsEnvironment[],
        enabled: Boolean(projectId && schedulerEnabled)
    });
    const accountsQuery = useQuery({
        queryKey: ["cost-optimizer", projectId, "aws-accounts"],
        queryFn: async () => {
            const { data: { awsAccounts } } = await getCloudService().getAwsAccounts(projectId);
            return awsAccounts.map(account => ({ id: account.id, name: account.accountName })) as SchedulerAwsAccount[];
        },
        enabled: Boolean(projectId && schedulerEnabled)
    });
    const schedulersQuery = useSchedulers(projectId, schedulerEnabled);
    const environments = useMemo(
        () => (environmentsQuery.data || []).filter(environment => environment.cloudProvider === "aws") as SchedulerEnvironment[],
        [environmentsQuery.data]
    );
    const awsAccounts = useMemo(() => accountsQuery.data || [], [accountsQuery.data]);
    const schedulers = useMemo(() => schedulersQuery.data || [], [schedulersQuery.data]);

    useEffect(() => {
        if (!selectedSchedulerId && schedulers.length) setSelectedSchedulerId(schedulers[0].environmentId);
        if (selectedSchedulerId && schedulers.length && !schedulers.some(item => item.environmentId === selectedSchedulerId)) {
            setSelectedSchedulerId(schedulers[0].environmentId);
        }
        if (!schedulers.length) setSelectedSchedulerId(undefined);
    }, [schedulers, selectedSchedulerId]);

    const listedScheduler = schedulers.find(item => item.environmentId === selectedSchedulerId);
    const resourcesQuery = useSchedulerResources(projectId, listedScheduler?.environmentId);
    const operationsQuery = useSchedulerOperations(projectId, listedScheduler?.environmentId);
    const scheduler = resourcesQuery.data?.scheduler || listedScheduler;
    const mutations = useSchedulerMutations(projectId, listedScheduler?.environmentId);
    const resources = resourcesQuery.data?.resources || [];
    const summary = resourcesQuery.data?.summary || EMPTY_SUMMARY;
    const busy = scheduler?.latestOperation?.status === "queued" || scheduler?.latestOperation?.status === "running";
    const selectedEnvironment = scheduler ? environments.find(item => item.id === scheduler.environmentId) : undefined;
    const selectedAccount = scheduler ? awsAccounts.find(item => item.id === scheduler.awsAccountId) : undefined;

    useEffect(() => {
        if (scheduler) setLeaseMinutes(scheduler.lease.defaultMinutes);
    }, [scheduler]);

    const filteredSchedulers = useMemo(() => {
        const query = schedulerSearch.trim().toLowerCase();
        if (!query) return schedulers;
        return schedulers.filter(item => {
            const environment = environments.find(candidate => candidate.id === item.environmentId);
            const account = awsAccounts.find(candidate => candidate.id === item.awsAccountId);
            return `${item.name} ${environment?.name || ""} ${account?.name || ""} ${item.awsAccountId} ${item.awsRegion}`
                .toLowerCase()
                .includes(query);
        });
    }, [schedulers, schedulerSearch, environments, awsAccounts]);

    const runMutation = async (action: () => Promise<unknown>, close: () => void, success: string) => {
        try {
            setMutationError("");
            await action();
            close();
            message.success(success);
        } catch (error) {
            setMutationError(errorMessage(error));
        }
    };

    const refresh = () => Promise.all([
        environmentsQuery.refetch(),
        accountsQuery.refetch(),
        schedulersQuery.refetch(),
        scheduler ? resourcesQuery.refetch() : Promise.resolve(),
        scheduler ? operationsQuery.refetch() : Promise.resolve()
    ]);

    const createScheduler = () => {
        setEditorScheduler(undefined);
        setEditorOpen(true);
    };

    const editScheduler = () => {
        if (!scheduler) return;
        setEditorScheduler(scheduler);
        setEditorOpen(true);
    };

    const removeScheduler = () => {
        if (!scheduler) return;
        Modal.confirm({
            title: `Delete ${scheduler.name}?`,
            content: "The scheduler must not own stopped resources. This removes its schedule and activity history.",
            okText: "Delete scheduler",
            okType: "danger",
            centered: true,
            onOk: async () => {
                await mutations.remove.mutateAsync();
                setSelectedSchedulerId(undefined);
                message.success("Scheduler deleted");
            }
        });
    };

    const detailMenu: MenuProps["items"] = [
        { key: "edit", label: "Edit scheduler", icon: <Pencil size={15} />, onClick: editScheduler },
        { type: "divider" },
        { key: "delete", label: "Delete scheduler", danger: true, icon: <Trash2 size={15} />, onClick: removeScheduler }
    ];

    const loading = environmentsQuery.isLoading || accountsQuery.isLoading || schedulersQuery.isLoading;

    const pageHeader = (subtitle: string, actions?: React.ReactNode) => (
        <CostPageHeader
            title="Resource schedules"
            description={<Text type="secondary">{subtitle}</Text>}
            icon={<CalendarClock size={21} />}
            navigation={navigation}
            actions={actions}
        />
    );

    if (!schedulerEnabled) {
        return (
            <div className="optimizer-shell">
                {pageHeader("Schedules are not available on this plan")}
                <div className="optimizer-centered-state">
                    <CalendarClock size={42} />
                    <Title level={3}>Resource scheduling is not included in this plan</Title>
                    <Text type="secondary">Upgrade the project plan to create automatic schedules and on-demand leases.</Text>
                </div>
            </div>
        );
    }

    if (schedulersQuery.error) {
        return (
            <div className="optimizer-shell">
                {pageHeader("Could not load schedules")}
                <div className="optimizer-centered-state">
                    <XCircle size={42} />
                    <Title level={3}>Could not load resource schedules</Title>
                    <Text type="secondary">{errorMessage(schedulersQuery.error)}</Text>
                    <Button type="primary" icon={<RefreshCw size={15} />} onClick={() => refresh()}>Try again</Button>
                </div>
            </div>
        );
    }

    return (
        <div className="optimizer-shell">
            {pageHeader(
                loading ? "Loading schedules..." : `${schedulers.length} ${schedulers.length === 1 ? "schedule" : "schedules"} across your AWS accounts`,
                <Space>
                    <Button
                        icon={<RefreshCw className={resourcesQuery.isFetching ? "optimizer-spin" : ""} size={15} />}
                        onClick={() => refresh()}
                        disabled={resourcesQuery.isFetching}
                    >
                        Refresh
                    </Button>
                    <Button type="primary" icon={<Plus size={16} />} onClick={createScheduler}>Create scheduler</Button>
                </Space>
            )}

            <main className="optimizer-page-content">
                {(environmentsQuery.error || accountsQuery.error) && (
                    <Alert
                        className="optimizer-page-alert"
                        type="warning"
                        showIcon
                        message="Some AWS targets could not be loaded"
                        description="Existing schedulers are still available. Refresh before creating a new one."
                    />
                )}

                {loading ? (
                    <div className="optimizer-loading-layout"><Skeleton active /><Skeleton active /></div>
                ) : schedulers.length === 0 ? (
                    <Card className="optimizer-empty-state">
                        <Empty
                            image={<span className="optimizer-empty-state__icon"><CalendarClock size={30} /></span>}
                            description={
                                <div><strong>No schedules yet</strong><Text type="secondary">Create one for a Microtica environment or a tagged resource group in any connected AWS account.</Text></div>
                            }
                        >
                            <Button type="primary" icon={<Plus size={16} />} onClick={createScheduler}>Create scheduler</Button>
                            {!awsAccounts.length && (
                                <Button type="link"><Link to={`/projects/${projectId}/integrations/cloud`}>Connect an AWS account</Link></Button>
                            )}
                        </Empty>
                    </Card>
                ) : (
                    <div className="optimizer-workspace">
                        <Card className="optimizer-scheduler-list" title="Schedulers" extra={<Tag>{schedulers.length}</Tag>}>
                            {schedulers.length > 4 && (
                                <Input
                                    className="optimizer-scheduler-search"
                                    allowClear
                                    prefix={<Search size={15} />}
                                    placeholder="Search schedules"
                                    value={schedulerSearch}
                                    onChange={event => setSchedulerSearch(event.target.value)}
                                />
                            )}
                            <div className="optimizer-scheduler-items">
                                {filteredSchedulers.map(item => {
                                    const itemEnvironment = environments.find(environment => environment.id === item.environmentId);
                                    const itemAccount = awsAccounts.find(account => account.id === item.awsAccountId);
                                    const status = statusDetails(item);
                                    const selected = item.environmentId === selectedSchedulerId;
                                    return (
                                        <button
                                            className={`optimizer-scheduler-item${selected ? " optimizer-scheduler-item--selected" : ""}`}
                                            key={item.environmentId}
                                            onClick={() => setSelectedSchedulerId(item.environmentId)}
                                        >
                                            <span className={`optimizer-scheduler-item__status optimizer-scheduler-item__status--${status.className}`} />
                                            <span className="optimizer-scheduler-item__body">
                                                <span><strong>{item.name}</strong><Tag>{itemEnvironment ? "Environment" : "AWS account"}</Tag></span>
                                                <small>{itemEnvironment?.name || itemAccount?.name || item.awsAccountId}</small>
                                                <small>{scheduleSummary(item)}</small>
                                            </span>
                                            <ChevronRight size={16} />
                                        </button>
                                    );
                                })}
                            </div>
                            {!filteredSchedulers.length && <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No schedules match your search" />}
                            <Button className="optimizer-list-create" type="dashed" icon={<Plus size={15} />} onClick={createScheduler}>Create scheduler</Button>
                        </Card>

                        {scheduler && (
                            <section className="optimizer-detail">
                                <Card className="optimizer-overview-card">
                                    <div className="optimizer-detail-header">
                                        <div>
                                            <div className="optimizer-detail-header__title">
                                                <Title level={2}>{scheduler.name}</Title>
                                                <Tag color={statusDetails(scheduler).color}>{statusDetails(scheduler).label}</Tag>
                                            </div>
                                            <Space className="optimizer-detail-meta" align="center" split={<Divider className="optimizer-meta-divider" type="vertical" />} wrap>
                                                <span><Cloud size={14} /> {selectedEnvironment?.name || selectedAccount?.name || "Connected AWS account"}</span>
                                                <span>{scheduler.awsAccountId}</span>
                                                <span>{scheduler.awsRegion}</span>
                                            </Space>
                                        </div>
                                        <Space>
                                            <Button icon={<Pencil size={15} />} onClick={editScheduler}>Edit</Button>
                                            <Dropdown menu={{ items: detailMenu }} trigger={["click"]}>
                                                <Button icon={<MoreHorizontal size={16} />} aria-label="More scheduler actions" />
                                            </Dropdown>
                                        </Space>
                                    </div>

                                    {!selectedEnvironment && (
                                        <div className="optimizer-resource-scope">
                                            <div><Server size={16} /><span><strong>Resource tag</strong><code>microtica:environment={scheduler.environmentId}</code></span></div>
                                            <Button
                                                type="text"
                                                size="small"
                                                icon={<Copy size={14} />}
                                                onClick={async () => {
                                                    await navigator.clipboard?.writeText(`microtica:environment=${scheduler.environmentId}`);
                                                    message.success("Resource tag copied");
                                                }}
                                            >
                                                Copy
                                            </Button>
                                        </div>
                                    )}

                                    {busy && (
                                        <Alert
                                            className="optimizer-operation-alert"
                                            type="info"
                                            showIcon
                                            message={`${scheduler.latestOperation?.action === "start" ? "Starting" : "Stopping"} resources`}
                                            description="AWS states refresh every five seconds while this action runs."
                                        />
                                    )}

                                    <div className={`optimizer-state-panel optimizer-state-panel--${statusDetails(scheduler).className}`}>
                                        <div className="optimizer-state-panel__copy">
                                            <span className="optimizer-state-panel__icon">
                                                {scheduler.state.desiredState === "running" ? <Play size={21} /> : <Square size={19} />}
                                            </span>
                                            <div>
                                                <Text type="secondary">Current target state</Text>
                                                <strong>{statusDetails(scheduler).label}</strong>
                                                <span>{summary.managed} managed resources · {summary.total} discovered</span>
                                            </div>
                                        </div>
                                        <Space>
                                            {scheduler.mode === "manual" && scheduler.state.expiresAt && (
                                                <Button icon={<TimerReset size={15} />} disabled={busy} onClick={() => { setMutationError(""); setExtendOpen(true); }}>Extend lease</Button>
                                            )}
                                            {scheduler.state.desiredState === "running" ? (
                                                <Button danger icon={<Square size={15} />} disabled={busy || summary.managed === 0} onClick={() => { setMutationError(""); setStopOpen(true); }}>Stop resources</Button>
                                            ) : (
                                                <Button type="primary" icon={<Play size={15} />} disabled={busy || summary.managed === 0} onClick={() => { setMutationError(""); setStartOpen(true); }}>Start resources</Button>
                                            )}
                                        </Space>
                                    </div>

                                    <div className="optimizer-stats">
                                        <Statistic title="Managed" value={summary.managed} />
                                        <Statistic title="Running" value={summary.running} valueStyle={{ color: "#237c3b" }} />
                                        <Statistic title="Stopped" value={summary.stopped} />
                                        <Statistic title="Needs attention" value={summary.transitioning + summary.failed} valueStyle={summary.failed ? { color: "#ae2f25" } : undefined} />
                                    </div>

                                    <div className="optimizer-schedule-card">
                                        <span className="optimizer-schedule-card__icon">
                                            {scheduler.mode === "automatic" ? <CalendarClock size={19} /> : <Clock3 size={19} />}
                                        </span>
                                        <div>
                                            <Text type="secondary">{scheduler.mode === "automatic" ? "Working hours" : "On-demand leases"}</Text>
                                            <strong>{scheduler.mode === "automatic"
                                                ? `${formatDays(scheduler.automaticSchedule.daysOfWeek)} · ${scheduler.automaticSchedule.startTime} to ${scheduler.automaticSchedule.stopTime}`
                                                : scheduler.state.expiresAt
                                                    ? `Lease ends ${formatDateTime(scheduler.state.expiresAt, scheduler.timezone)}`
                                                    : `Default lease ${scheduler.lease.defaultMinutes / 60} hours · no active lease`}
                                            </strong>
                                            <span>{scheduler.timezone}{scheduler.mode === "manual" ? ` · safety stop ${scheduler.lease.safetyStopTime}` : ""}</span>
                                        </div>
                                        <Button type="link" onClick={editScheduler}>Change schedule</Button>
                                    </div>
                                </Card>

                                {resourcesQuery.error && <Alert type="error" showIcon message="Could not load AWS resources" description={errorMessage(resourcesQuery.error)} />}
                                {operationsQuery.error && <Alert type="error" showIcon message="Could not load recent activity" description={errorMessage(operationsQuery.error)} />}
                                {resourcesQuery.data?.warnings?.map(warning => <Alert type="warning" showIcon message={warning} key={warning} />)}

                                <ResourceInventory resources={resources} loading={resourcesQuery.isLoading} />
                                <RecentActivity operations={operationsQuery.data || []} loading={operationsQuery.isLoading} />
                            </section>
                        )}
                    </div>
                )}
            </main>

            <ScheduleEditorDialog
                environments={environments}
                awsAccounts={awsAccounts}
                existingSchedulerIds={schedulers.map(item => item.environmentId)}
                scheduler={editorScheduler}
                open={editorOpen}
                saving={mutations.save.isPending}
                onOpenChange={setEditorOpen}
                onSave={async (targetEnvironmentId, request) => {
                    await mutations.save.mutateAsync({ targetEnvironmentId, request });
                    setSelectedSchedulerId(targetEnvironmentId);
                    message.success(editorScheduler ? "Scheduler updated" : "Scheduler created");
                }}
            />

            <Modal
                title={`Start ${summary.managed} managed resources?`}
                open={startOpen}
                centered
                okText="Start resources"
                okButtonProps={{ disabled: mutations.start.isPending }}
                confirmLoading={mutations.start.isPending}
                onCancel={() => setStartOpen(false)}
                onOk={() => runMutation(
                    () => mutations.start.mutateAsync(scheduler?.mode === "manual" ? leaseMinutes : undefined),
                    () => setStartOpen(false),
                    "Start queued"
                )}
            >
                <Text type="secondary">Only resources previously stopped by this scheduler are restored.</Text>
                {scheduler?.mode === "manual" && (
                    <div className="optimizer-modal-field">
                        <label>Lease duration</label>
                        <Select
                            value={leaseMinutes}
                            onChange={setLeaseMinutes}
                            options={[60, 120, 240, 480, 720, 1440].map(value => ({ value, label: value === 60 ? "1 hour" : `${value / 60} hours` }))}
                        />
                        <Text type="secondary">Resources stop again when the lease expires or at the daily safety stop.</Text>
                    </div>
                )}
                {mutationError && <Alert className="optimizer-form-error" type="error" showIcon message={mutationError} />}
            </Modal>

            <Modal
                title={`Stop ${summary.managed} managed resources?`}
                open={stopOpen}
                centered
                okText="Stop resources"
                okButtonProps={{ danger: true }}
                confirmLoading={mutations.stop.isPending}
                onCancel={() => setStopOpen(false)}
                onOk={() => runMutation(() => mutations.stop.mutateAsync(), () => setStopOpen(false), "Stop queued")}
            >
                <Alert
                    type="warning"
                    showIcon
                    icon={<AlertTriangle size={17} />}
                    message="The scheduler saves current ECS, EKS, and Auto Scaling capacity before stopping."
                    description="Resources that were already stopped stay stopped when this scheduler starts the group again."
                />
                {mutationError && <Alert className="optimizer-form-error" type="error" showIcon message={mutationError} />}
            </Modal>

            <Modal
                title="Extend active lease"
                open={extendOpen}
                centered
                okText="Extend lease"
                confirmLoading={mutations.extend.isPending}
                onCancel={() => setExtendOpen(false)}
                onOk={() => runMutation(() => mutations.extend.mutateAsync(leaseMinutes), () => setExtendOpen(false), "Lease extended")}
            >
                <div className="optimizer-modal-field">
                    <label>Additional time</label>
                    <Select
                        value={leaseMinutes}
                        onChange={setLeaseMinutes}
                        options={[30, 60, 120, 240, 480].map(value => ({ value, label: value < 60 ? `${value} minutes` : `${value / 60} hours` }))}
                    />
                    <Text type="secondary">The lease cannot extend beyond 24 hours from now.</Text>
                </div>
                {mutationError && <Alert className="optimizer-form-error" type="error" showIcon message={mutationError} />}
            </Modal>
        </div>
    );
}
